/**
 * Quick Order JavaScript - Team Line Wholesale Theme
 * 
 * Features:
 * - Client-side filtering (search, brand, category, in-stock)
 * - Batch add to cart functionality
 * - Summary bar updates
 * - Cart drawer integration
 * - BACKORDER SUPPORT: Split orders between in-stock and backorder variants
 */

(function() {
  'use strict';

  // ============================================================
  // SAFE JSON PARSER - Prevents "Unexpected end of JSON input" errors
  // ============================================================

  /**
   * Safely parse JSON from a fetch response.
   * - Reads response as text first to avoid JSON parse errors on empty/non-JSON responses.
   * 
   * @param {Response} res - The fetch response object
   * @returns {Promise<Object|null>} Parsed JSON or error object
   */
  async function safeJson(res) {
    const text = await res.text();
    if (!text || text.trim() === '') {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      console.warn('[QUICK-ORDER] Non-JSON response received:', text.substring(0, 200));
      return { _nonJson: true, _raw: text.substring(0, 500) };
    }
  }

  /**
   * Extract a meaningful error message from cart API response data.
   */
  function extractErrorMessage(data, status) {
    if (!data) {
      return `Request failed with status ${status} (empty response)`;
    }
    if (data._nonJson) {
      return `Request failed with status ${status} (non-JSON response, see console)`;
    }
    if (data.description) return data.description;
    if (data.message) return data.message;
    if (data.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    if (data.errors) {
      if (typeof data.errors === 'string') return data.errors;
      if (Array.isArray(data.errors)) return data.errors.join(', ');
      return JSON.stringify(data.errors);
    }
    return `Request failed with status ${status}`;
  }

  /**
   * Log detailed debug information when a cart request fails.
   */
  function logCartRequestFailure(endpoint, method, status, payload, responseData) {
    console.group(`[QUICK-ORDER] Request Failed: ${method} ${endpoint}`);
    console.log('HTTP Status:', status);
    if (payload) {
      console.log('Request Payload:', payload);
    }
    if (responseData) {
      if (responseData._nonJson) {
        console.log('Response (non-JSON):', responseData._raw);
      } else {
        console.log('Response Data:', responseData);
      }
    } else {
      console.log('Response: (empty)');
    }
    console.groupEnd();
  }

  // ============================================================
  // BACKORDER CONFIGURATION (Future-proofing)
  // ============================================================
  const BACKORDER_CONFIG = {
    BACKORDER_CAP_ENABLED: false,  // Set true to enable cap
    BACKORDER_CAP_QTY: 0           // Max backorder qty per line (0 = unlimited when disabled)
  };

  // ============================================================
  // DOM Elements
  // ============================================================
  const table = document.querySelector('[data-quick-order-table]');
  const tableBody = document.querySelector('[data-quick-order-body]');
  const searchInput = document.querySelector('[data-quick-order-search]');
  const brandFilter = document.querySelector('[data-filter-brand]');
  const categoryFilter = document.querySelector('[data-filter-category]');
  const inStockCheckbox = document.querySelector('[data-filter-in-stock]');
  const addAllButton = document.querySelector('[data-add-all-to-cart]');
  const summaryItems = document.querySelector('[data-summary-items]');
  const summaryQuantity = document.querySelector('[data-summary-quantity]');
  const summarySubtotal = document.querySelector('[data-summary-subtotal]');
  const resultsCount = document.querySelector('[data-results-count]');
  const resultsText = document.querySelector('[data-results-text]');

  if (!table || !tableBody) return;

  // Get all product rows
  let allRows = Array.from(tableBody.querySelectorAll('.quick-order__row'));

  /**
   * Filter rows based on current filter state
   */
  function filterRows() {
    const searchTerm = searchInput ? searchInput.value.toLowerCase().trim() : '';
    const selectedBrand = brandFilter ? brandFilter.value.trim() : '';
    const selectedCategory = categoryFilter ? categoryFilter.value.trim() : '';
    const inStockOnly = inStockCheckbox ? inStockCheckbox.checked : false;

    let visibleCount = 0;

    allRows.forEach(row => {
      let visible = true;

      // Search filter (product name or SKU)
      if (searchTerm) {
        const productTitle = row.querySelector('.quick-order__product-title');
        const productVendor = row.querySelector('.quick-order__product-vendor');
        const productSku = row.querySelector('.quick-order__product-sku');
        
        const titleText = productTitle ? productTitle.textContent.toLowerCase().trim() : '';
        const vendorText = productVendor ? productVendor.textContent.toLowerCase().trim() : '';
        const skuText = productSku ? productSku.textContent.toLowerCase().trim() : '';
        
        // Search in title, vendor, or SKU
        const matchFound = titleText.includes(searchTerm) || 
                          vendorText.includes(searchTerm) || 
                          skuText.includes(searchTerm);
        
        if (!matchFound) {
          visible = false;
        }
      }

      // Brand filter (empty value = "All Brands" = show all)
      if (selectedBrand && visible) {
        const rowBrand = row.dataset.vendor ? row.dataset.vendor.trim() : '';
        if (rowBrand.toLowerCase() !== selectedBrand.toLowerCase()) {
          visible = false;
        }
      }

      // Category filter (empty value = "All Categories" = show all)
      if (selectedCategory && visible) {
        const rowCategory = row.dataset.type ? row.dataset.type.trim() : '';
        if (rowCategory.toLowerCase() !== selectedCategory.toLowerCase()) {
          visible = false;
        }
      }

      // In stock only filter
      if (inStockOnly && visible) {
        const isAvailable = row.dataset.available === 'true';
        if (!isAvailable) {
          visible = false;
        }
      }

      // Show/hide row
      if (visible) {
        row.style.display = '';
        visibleCount++;
      } else {
        row.style.display = 'none';
      }
    });

    // Update "Add All" button state
    updateAddAllButtonState();

    // Update results count display
    updateResultsCount(visibleCount, allRows.length);

    return visibleCount;
  }

  /**
   * Update the results count indicator
   */
  function updateResultsCount(visible, total) {
    if (!resultsCount || !resultsText) return;

    const searchTerm = searchInput ? searchInput.value.trim() : '';
    const selectedBrand = brandFilter ? brandFilter.value.trim() : '';
    const selectedCategory = categoryFilter ? categoryFilter.value.trim() : '';
    const inStockOnly = inStockCheckbox ? inStockCheckbox.checked : false;

    const isFiltered = searchTerm || selectedBrand || selectedCategory || inStockOnly;

    if (isFiltered && visible < total) {
      resultsCount.style.display = '';
      resultsText.textContent = `Showing ${visible} of ${total} products`;
    } else {
      resultsCount.style.display = 'none';
    }
  }

  /**
   * Clamp quantity input to valid range [0, max]
   * BACKORDER AWARE: For backorder-enabled products, don't clamp if backorder variant exists
   */
  function clampQuantityInput(input) {
    if (!input) return;

    const row = input.closest('.quick-order__row');
    const allowBackorder = row?.dataset.allowBackorder === 'true';
    const backorderVariantId = row?.dataset.backorderVariantId;
    const hasBackorderVariant = backorderVariantId && backorderVariantId !== '';

    const min = parseInt(input.getAttribute('min')) || 0;
    const maxAttr = input.getAttribute('data-max-qty');
    const max = maxAttr ? parseInt(maxAttr) : 999999;
    let value = parseInt(input.value);

    // If value is empty or NaN, set to 0
    if (isNaN(value) || input.value === '') {
      input.value = 0;
      updateBackorderHelper(row, 0);
      return;
    }

    // Clamp value between min and max
    if (value < min) {
      value = min;
    }
    
    // BACKORDER: Only clamp to max if backorder is NOT allowed
    if (!allowBackorder || !hasBackorderVariant) {
      if (value > max) {
      value = max;
      }
    }

    // Update input if value changed
    if (parseInt(input.value) !== value) {
      input.value = value;
    }

    // Update backorder helper messaging
    updateBackorderHelper(row, value);
  }

  /**
   * BACKORDER: Update the helper text under quantity input showing split
   */
  function updateBackorderHelper(row, requestedQty) {
    if (!row) return;

    const helper = row.querySelector('[data-quantity-helper]');
    if (!helper) return;

    const allowBackorder = row.dataset.allowBackorder === 'true';
    const backorderVariantId = row.dataset.backorderVariantId;
    const hasBackorderVariant = backorderVariantId && backorderVariantId !== '';
    const stock = parseInt(row.dataset.stock) || 0;

    // Reset state
    helper.style.display = 'none';
    helper.textContent = '';
    helper.className = 'quick-order__quantity-helper';
    row.classList.remove('quick-order__row--backorder-warning');

    if (requestedQty <= 0) return;

    // Calculate split
    const inStockQty = Math.min(requestedQty, stock);
    const backorderQty = Math.max(0, requestedQty - stock);

    if (backorderQty > 0) {
      if (allowBackorder && hasBackorderVariant) {
        // Show split messaging
        helper.innerHTML = `<span class="quick-order__helper-stock">${inStockQty} in stock</span> • <span class="quick-order__helper-backorder">${backorderQty} on backorder</span>`;
        helper.classList.add('quick-order__quantity-helper--split');
        helper.style.display = 'block';
        row.classList.add('quick-order__row--backorder-warning');
      } else if (allowBackorder && !hasBackorderVariant) {
        // Configuration error - already shown in liquid
        helper.textContent = `Only ${stock} available (backorder variant missing)`;
        helper.classList.add('quick-order__quantity-helper--error');
        helper.style.display = 'block';
      } else {
        // Not allowed - show warning
        helper.textContent = `Only ${stock} available`;
        helper.classList.add('quick-order__quantity-helper--error');
        helper.style.display = 'block';
      }
    }
  }

  /**
   * Update the "Add All to Cart" button enabled/disabled state
   */
  function updateAddAllButtonState() {
    if (!addAllButton) return;

    const hasQuantity = allRows.some(row => {
      const input = row.querySelector('[data-quantity-input]');
      return input && parseInt(input.value || 0) > 0;
    });

    addAllButton.disabled = !hasQuantity;
  }

  /**
   * Collect items to add to cart (where quantity > 0)
   * BACKORDER AWARE: Splits quantities between in-stock and backorder variants
   */
  function collectCartItems() {
    const items = [];
    const errors = [];

    allRows.forEach(row => {
      const input = row.querySelector('[data-quantity-input]');
      if (!input || input.disabled) return;
      
      const rawQty = parseInt(input.value) || 0;
      if (rawQty <= 0) return;

        const variantId = input.dataset.variantId;
      if (!variantId) return;

      // BACKORDER: Read row data attributes
      const allowBackorder = row.dataset.allowBackorder === 'true';
      const backorderVariantId = row.dataset.backorderVariantId;
      const hasBackorderVariant = backorderVariantId && backorderVariantId !== '';
      const stock = parseInt(row.dataset.stock) || 0;

      // Calculate split
      const inStockQty = Math.min(rawQty, stock);
      const backorderQty = Math.max(0, rawQty - stock);

      if (backorderQty > 0) {
        // Quantity exceeds stock
        if (allowBackorder) {
          if (hasBackorderVariant) {
            // BACKORDER CAP CHECK (future-proofing)
            let finalBackorderQty = backorderQty;
            if (BACKORDER_CONFIG.BACKORDER_CAP_ENABLED && BACKORDER_CONFIG.BACKORDER_CAP_QTY > 0) {
              if (backorderQty > BACKORDER_CONFIG.BACKORDER_CAP_QTY) {
                const productTitle = row.querySelector('.quick-order__product-title')?.textContent?.trim() || 'Product';
                errors.push(`${productTitle}: Maximum backorder is ${BACKORDER_CONFIG.BACKORDER_CAP_QTY} units`);
                finalBackorderQty = BACKORDER_CONFIG.BACKORDER_CAP_QTY;
              }
            }

            // Add in-stock portion (if any)
            if (inStockQty > 0) {
          items.push({
            id: variantId,
                quantity: inStockQty,
                _isBackorder: false
              });
            }

            // Add backorder portion with line item properties
            items.push({
              id: backorderVariantId,
              quantity: finalBackorderQty,
              _isBackorder: true,
              properties: {
                '_purchase_type': 'backorder',
                '_original_variant_id': variantId,
                '_backorder_note': 'Ships when stock arrives',
                '_in_stock_qty': String(inStockQty),
                '_backorder_qty': String(finalBackorderQty)
              }
          });
          } else {
            // Configuration error - backorder variant missing
            const productTitle = row.querySelector('.quick-order__product-title')?.textContent?.trim() || 'Product';
            errors.push(`${productTitle}: Backorder variant not configured. Only ${stock} available.`);
            // Add only available stock
            if (inStockQty > 0) {
              items.push({
                id: variantId,
                quantity: inStockQty,
                _isBackorder: false
              });
            }
          }
        } else {
          // Not allowed to backorder - clamp to stock
          const productTitle = row.querySelector('.quick-order__product-title')?.textContent?.trim() || 'Product';
          if (stock > 0) {
            errors.push(`${productTitle}: Only ${stock} available (added ${stock} instead of ${rawQty})`);
            items.push({
              id: variantId,
              quantity: stock,
              _isBackorder: false
            });
          } else {
            errors.push(`${productTitle}: Out of stock`);
          }
        }
      } else {
        // Quantity within stock - add normally
        items.push({
          id: variantId,
          quantity: rawQty,
          _isBackorder: false
        });
      }
    });

    return { items, errors };
  }

  /**
   * Add items to cart via AJAX
   * BACKORDER AWARE: Preserves line item properties for backorder items
   */
  async function addToCart(items) {
    if (!items || items.length === 0) {
      console.warn('No items to add to cart');
      return null;
    }

    // Shopify can reject large single-line quantities; break into chunks
    // IMPORTANT: Preserve properties when chunking backorder items
    const expandedItems = [];
    const MAX_QTY_PER_LINE = 100; // safer chunk size while still respecting typical request limits
    
    items.forEach(item => {
      let remaining = parseInt(item.quantity, 10) || 0;
      const id = item.id;
      const properties = item.properties || null;
      
      while (remaining > 0) {
        const chunk = Math.min(remaining, MAX_QTY_PER_LINE);
        const cartItem = { id, quantity: chunk };
        
        // BACKORDER: Preserve line item properties
        if (properties) {
          cartItem.properties = properties;
        }
        
        expandedItems.push(cartItem);
        remaining -= chunk;
      }
    });

    if (expandedItems.length === 0) return null;

    // Send in small batches to avoid payload limits
    const BATCH_SIZE = 20;
    const batches = [];
    for (let i = 0; i < expandedItems.length; i += BATCH_SIZE) {
      batches.push(expandedItems.slice(i, i + BATCH_SIZE));
    }

    let lastResponse = null;
    const endpoint = '/cart/add.js';

    try {
      for (const batch of batches) {
        const payload = { items: batch };
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });

        const data = await safeJson(response);

        if (!response.ok) {
          logCartRequestFailure(endpoint, 'POST', response.status, payload, data);
          throw new Error(extractErrorMessage(data, response.status));
        }

        lastResponse = data;
      }

      return lastResponse;
    } catch (error) {
      console.error('[QUICK-ORDER] Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Get current cart data
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return await safeJson(response);
    } catch (error) {
      console.error('[QUICK-ORDER] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Update the sticky summary bar with cart data
   */
  function updateSummaryBar(cart) {
    if (!cart) return;

    // Update items count
    if (summaryItems) {
      summaryItems.textContent = cart.item_count || 0;
    }

    // Update total quantity
    if (summaryQuantity) {
      const totalQuantity = cart.items ? cart.items.reduce((sum, item) => sum + item.quantity, 0) : 0;
      summaryQuantity.textContent = totalQuantity;
    }

    // Update subtotal
    if (summarySubtotal) {
      const subtotal = cart.total_price || 0;
      // Format as money (simple approach - adjust currency as needed)
      summarySubtotal.textContent = formatMoney(subtotal);
    }
  }

  /**
   * Format money (basic implementation)
   * Note: This is a simplified version. Real implementation should use Shopify's money format.
   */
  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Parse money string to cents
   * Handles formats like: $10.50, $1,234.56, $71.40 NZD, etc.
   */
  function parseMoney(moneyString) {
    if (!moneyString || typeof moneyString !== 'string') return 0;
    
    // Remove currency symbols, letters, and whitespace
    // Keep only digits, commas, and decimal points
    const cleaned = moneyString.replace(/[^0-9.,]/g, '');
    
    // Remove commas (thousand separators)
    const withoutCommas = cleaned.replace(/,/g, '');
    
    // Parse as float and convert to cents
    const dollars = parseFloat(withoutCommas) || 0;
    return Math.round(dollars * 100);
  }

  /**
   * Calculate and update live summary from current quantities
   * Reads BSS B2B-adjusted prices from the DOM
   */
  function calculateLiveSummary() {
    let totalItems = 0;
    let totalQuantity = 0;
    let subtotalCents = 0;

    allRows.forEach(row => {
      const input = row.querySelector('[data-quantity-input]');
      if (!input || input.disabled) return;

      const quantity = parseInt(input.value || 0);
      if (quantity <= 0) return;

      // Read the displayed price (BSS B2B may have adjusted it)
      const priceElement = row.querySelector('.quick-order__price');
      if (!priceElement) return;

      const priceText = priceElement.textContent.trim();
      const priceCents = parseMoney(priceText);

      // Calculate line total
      const lineTotal = priceCents * quantity;

      totalItems++;
      totalQuantity += quantity;
      subtotalCents += lineTotal;
    });

    // Update summary display
    if (summaryItems) {
      summaryItems.textContent = totalItems;
    }

    if (summaryQuantity) {
      summaryQuantity.textContent = totalQuantity;
    }

    if (summarySubtotal) {
      summarySubtotal.textContent = formatMoney(subtotalCents);
    }
  }

  /**
   * Navigate to the full cart page (cart drawer is disabled for B2B)
   */
  async function goToCartPage() {
    await updateCartCount();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    const cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
    window.location.href = cartUrl;
  }

  /**
   * Update cart count in header
   */
  async function updateCartCount() {
    const cart = await getCart();
    if (!cart) return;

    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = cart.item_count > 0 ? cart.item_count : '';
    });
  }

  /**
   * Reset all quantity inputs to 0
   */
  function resetQuantityInputs() {
    allRows.forEach(row => {
      const input = row.querySelector('[data-quantity-input]');
      if (input && !input.disabled) {
        input.value = 0;
      }
    });
    updateAddAllButtonState();
    calculateLiveSummary();
  }

  /**
   * Show loading state on "Add All" button
   */
  function setAddAllLoading(isLoading) {
    if (!addAllButton) return;

    if (isLoading) {
      addAllButton.disabled = true;
      addAllButton.dataset.originalText = addAllButton.textContent;
      addAllButton.textContent = 'Adding to Cart...';
      addAllButton.classList.add('is-loading');
    } else {
      addAllButton.classList.remove('is-loading');
      if (addAllButton.dataset.originalText) {
        addAllButton.textContent = addAllButton.dataset.originalText;
        delete addAllButton.dataset.originalText;
      }
      updateAddAllButtonState();
    }
  }

  /**
   * Show success message
   */
  function showSuccessMessage(itemCount) {
    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.className = 'quick-order-toast quick-order-toast--success';
    toast.textContent = `✓ Added ${itemCount} item${itemCount !== 1 ? 's' : ''} to cart`;
    toast.style.cssText = `
      position: fixed;
      top: 100px;
      right: 20px;
      background: #065f46;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      animation: slideInRight 0.3s ease;
    `;
    
    document.body.appendChild(toast);

    // Add animation keyframes if not already present
    if (!document.getElementById('quick-order-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'quick-order-toast-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(400px); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Show error message
   */
  function showErrorMessage(message) {
    const toast = document.createElement('div');
    toast.className = 'quick-order-toast quick-order-toast--error';
    toast.textContent = `✕ ${message}`;
    toast.style.cssText = `
      position: fixed;
      top: 100px;
      right: 20px;
      background: #991b1b;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      animation: slideInRight 0.3s ease;
    `;
    
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  /**
   * Handle "Add All to Cart" button click
   * BACKORDER AWARE: Shows warnings for any backorder splits or errors
   */
  async function handleAddAllToCart() {
    const { items, errors } = collectCartItems();

    if (items.length === 0 && errors.length === 0) {
      showErrorMessage('Please enter quantities for at least one product');
      return;
    }

    // Show errors/warnings if any
    if (errors.length > 0) {
      // Show first 3 errors max
      const displayErrors = errors.slice(0, 3);
      const remainingCount = errors.length - 3;
      let errorMsg = displayErrors.join('\n');
      if (remainingCount > 0) {
        errorMsg += `\n...and ${remainingCount} more`;
      }
      showWarningMessage(errorMsg);
    }

    if (items.length === 0) {
      showErrorMessage('No items could be added due to stock limitations');
      return;
    }

    setAddAllLoading(true);

    try {
      // Count backorder items for messaging
      const backorderCount = items.filter(item => item._isBackorder).length;
      const inStockCount = items.filter(item => !item._isBackorder).length;

      // Add items to cart
      await addToCart(items);

      // Get updated cart and update summary bar
      const cart = await getCart();
      if (cart) {
        updateSummaryBar(cart);
      }

      // Show success message with backorder note if applicable
      if (backorderCount > 0) {
        showSuccessMessage(`Added ${inStockCount} in-stock + ${backorderCount} backorder items`);
      } else {
      showSuccessMessage(items.length);
      }

      // Reset quantity inputs
      resetQuantityInputs();

      // Go to full cart page (drawer disabled)
      await goToCartPage();

    } catch (error) {
      showErrorMessage(error.message || 'Failed to add items to cart');
    } finally {
      setAddAllLoading(false);
    }
  }

  /**
   * Show warning message (yellow toast)
   */
  function showWarningMessage(message) {
    const toast = document.createElement('div');
    toast.className = 'quick-order-toast quick-order-toast--warning';
    toast.style.cssText = `
      position: fixed;
      top: 100px;
      right: 20px;
      background: #92400e;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      animation: slideInRight 0.3s ease;
      max-width: 400px;
      white-space: pre-line;
      font-size: 0.875rem;
    `;
    toast.textContent = `⚠ ${message}`;
    
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 6000);
  }

  // Event Listeners
  if (searchInput) {
    searchInput.addEventListener('input', filterRows);
    searchInput.addEventListener('keyup', filterRows);
  }

  if (brandFilter) {
    brandFilter.addEventListener('change', filterRows);
  }

  if (categoryFilter) {
    categoryFilter.addEventListener('change', filterRows);
  }

  if (inStockCheckbox) {
    inStockCheckbox.addEventListener('change', filterRows);
  }

  if (addAllButton) {
    addAllButton.addEventListener('click', handleAddAllToCart);
  }

  // Listen to quantity input changes to update button state and clamp values
  allRows.forEach(row => {
    const input = row.querySelector('[data-quantity-input]');
    if (input) {
      // Clamp on input (as user types)
      input.addEventListener('input', function() {
        clampQuantityInput(input);
        updateAddAllButtonState();
        calculateLiveSummary();
      });
      
      // Clamp on change (when value changes and field loses focus)
      input.addEventListener('change', function() {
        clampQuantityInput(input);
        updateAddAllButtonState();
        calculateLiveSummary();
      });
      
      // Also clamp on blur (when user leaves the field)
      input.addEventListener('blur', function() {
        clampQuantityInput(input);
        calculateLiveSummary();
      });
      
      // Prevent mousewheel from changing quantity (can bypass limits)
      input.addEventListener('wheel', function(e) {
        e.preventDefault();
      });
    }
  });

  /**
   * Infinite Scroll Implementation
   */
  const section = document.querySelector('[data-section-type="quick-order"]');
  const loadingIndicator = document.querySelector('[data-loading-indicator]');
  const scrollSentinel = document.querySelector('[data-scroll-sentinel]');
  
  let currentOffset = 0;
  let isLoading = false;
  let hasMoreProducts = true;
  let collections = [];
  
  // Get collection handles from section data attributes
  if (section) {
    const col1 = section.dataset.collection1;
    const col2 = section.dataset.collection2;
    const col3 = section.dataset.collection3;
    
    if (col1) collections.push(col1);
    if (col2) collections.push(col2);
    if (col3) collections.push(col3);
  }
  
  // Count initially loaded variants
  currentOffset = allRows.length;
  
  /**
   * Load more products via AJAX
   */
  async function loadMoreProducts() {
    if (isLoading || !hasMoreProducts || collections.length === 0) return;
    
    isLoading = true;
    if (loadingIndicator) loadingIndicator.style.display = 'flex';
    
    try {
      const batchSize = 50; // Load 50 more variants at a time
      let variantsLoaded = 0;
      
      // Fetch products from each collection
      for (const collectionHandle of collections) {
        if (variantsLoaded >= batchSize) break;
        
        // Calculate which page to fetch (Shopify collections paginate at 50 products per page)
        const productsPerPage = 50;
        const skip = Math.floor(currentOffset / productsPerPage) * productsPerPage;
        const page = Math.floor(skip / productsPerPage) + 1;
        
        const url = `/collections/${collectionHandle}/products.json?limit=${productsPerPage}&page=${page}`;
        const response = await fetch(url);
        
        if (!response.ok) continue;
        
        const data = await safeJson(response);
        if (!data) continue;
        const products = data.products || [];
        
        if (products.length === 0) continue;
        
        // Render products into table
        products.forEach(product => {
          product.variants.forEach(variant => {
            if (variantsLoaded >= batchSize) return;
            
            const row = createVariantRow(product, variant);
            if (row) {
              tableBody.appendChild(row);
              allRows.push(row);
              variantsLoaded++;
              currentOffset++;
              
              // Attach event listeners to the new row's quantity input
              const input = row.querySelector('[data-quantity-input]');
              if (input) {
                input.addEventListener('input', function() {
                  clampQuantityInput(input);
                  updateAddAllButtonState();
                  calculateLiveSummary();
                });
                input.addEventListener('change', function() {
                  clampQuantityInput(input);
                  updateAddAllButtonState();
                  calculateLiveSummary();
                });
                input.addEventListener('blur', function() {
                  clampQuantityInput(input);
                  calculateLiveSummary();
                });
                input.addEventListener('wheel', function(e) {
                  e.preventDefault();
                });
              }
            }
          });
        });
        
        // If we got fewer products than expected, we might be at the end
        if (products.length < productsPerPage) {
          hasMoreProducts = false;
        }
      }
      
      if (variantsLoaded === 0) {
        hasMoreProducts = false;
      }
      
      // Re-filter to apply current filters to new rows
      filterRows();
      
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      isLoading = false;
      if (loadingIndicator) loadingIndicator.style.display = 'none';
    }
  }
  
  /**
   * Create a variant row HTML element from product and variant data
   * BACKORDER AWARE: Skips backorder variants and adds backorder data attributes
   */
  function createVariantRow(product, variant) {
    // Check if this variant has already been rendered (prevents duplicates across collections)
    const existingRow = tableBody.querySelector(`tr[data-variant-id="${variant.id}"]`);
    if (existingRow) {
      return null;
    }
    
    // BACKORDER: Skip backorder variants (they're not shown in the UI)
    const variantTitleLower = (variant.title || '').toLowerCase();
    if (variant.inventory_management !== 'shopify' && variantTitleLower.includes('backorder')) {
      return null;
    }

    const showThumbnails = section?.dataset.showThumbnails === 'true';
    const showVendor = section?.dataset.showVendor === 'true';
    const showSku = section?.dataset.showSku === 'true';
    
    const variantAvailable = variant.available;
    let variantTitle = product.title;
    if (product.variants.length > 1 && variant.title !== 'Default Title') {
      variantTitle = `${product.title} - ${variant.title}`;
    }
    
    // BACKORDER: Check if product allows backorder
    const productTags = product.tags || [];
    const allowBackorder = productTags.some(tag => tag.toLowerCase() === 'allow_backorder');
    
    // BACKORDER: Find backorder variant
    let backorderVariantId = '';
    if (allowBackorder && product.variants) {
      const backorderVariant = product.variants.find(v => {
        const vTitleLower = (v.title || '').toLowerCase();
        return v.inventory_management !== 'shopify' && vTitleLower.includes('backorder');
      });
      if (backorderVariant) {
        backorderVariantId = backorderVariant.id;
      }
    }
    const hasBackorderVariant = backorderVariantId !== '';

    // Calculate stock
    let variantStock = 999999;
    if (variant.inventory_management === 'shopify') {
      variantStock = variant.inventory_quantity || 0;
    }
    
    // Calculate max quantity (for non-backorder clamping)
    let maxQty = 999999;
    if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
      maxQty = variant.inventory_quantity > 0 ? variant.inventory_quantity : 0;
    }

    // BACKORDER: Determine if input should be enabled
    let inputEnabled = variantAvailable;
    if (allowBackorder && hasBackorderVariant) {
      inputEnabled = true; // Enable even if out of stock
    }
    
    // Create row
    const row = document.createElement('tr');
    row.className = 'quick-order__row';
    row.dataset.productId = product.id;
    row.dataset.variantId = variant.id;
    row.dataset.vendor = product.vendor || '';
    row.dataset.type = product.type || '';
    row.dataset.available = variantAvailable;
    // BACKORDER: Add backorder data attributes
    row.dataset.allowBackorder = allowBackorder;
    row.dataset.backorderVariantId = backorderVariantId;
    row.dataset.stock = variantStock;
    
    // Build row HTML
    let html = '<td class="quick-order__cell quick-order__cell--product"><div class="quick-order__product-info">';
    
    // Thumbnail
    if (showThumbnails && (variant.featured_image || product.featured_image)) {
      const image = variant.featured_image || product.featured_image;
      html += `<div class="quick-order__product-image">
        <img src="${image.src}&width=60" alt="${image.alt || product.title}" loading="lazy" width="60" height="60">
      </div>`;
    }
    
    // Product details
    html += '<div class="quick-order__product-details">';
    html += `<a href="/products/${product.handle}?variant=${variant.id}" class="quick-order__product-title">${variantTitle}</a>`;
    if (showVendor && product.vendor) {
      html += `<div class="quick-order__product-vendor">${product.vendor}</div>`;
    }
    if (showSku && variant.sku) {
      html += `<div class="quick-order__product-sku">SKU: ${variant.sku}</div>`;
    }
    html += '</div></div></td>';
    
    // Pack/Unit column
    html += '<td class="quick-order__cell quick-order__cell--pack">';
    html += '<span class="quick-order__pack-default">Each</span>'; // Could add metafield support later
    html += '</td>';
    
    // Availability column - BACKORDER aware
    html += '<td class="quick-order__cell quick-order__cell--availability">';
    if (variantAvailable) {
      if (variant.inventory_management === 'shopify') {
        if (variant.inventory_quantity > 50) {
          html += '<span class="quick-order__availability quick-order__availability--in-stock">In Stock</span>';
        } else if (variant.inventory_quantity > 0) {
          html += `<span class="quick-order__availability quick-order__availability--low-stock">Only ${variant.inventory_quantity} left</span>`;
        } else {
          html += '<span class="quick-order__availability quick-order__availability--in-stock">In Stock</span>';
        }
      } else {
        html += '<span class="quick-order__availability quick-order__availability--in-stock">In Stock</span>';
      }
    } else {
      // BACKORDER: Show "Backorder Available" if applicable
      if (allowBackorder && hasBackorderVariant) {
        html += '<span class="quick-order__availability quick-order__availability--backorder">Backorder Available</span>';
    } else {
      html += '<span class="quick-order__availability quick-order__availability--out-of-stock">Out of Stock</span>';
      }
    }
    html += '</td>';
    
    // Price column
    const priceFormatted = formatMoney(variant.price);
    html += `<td class="quick-order__cell quick-order__cell--price">
      <div class="quick-order__price" data-price="${variant.price}">${priceFormatted}</div>
    </td>`;
    
    // Quantity column - BACKORDER aware
    html += `<td class="quick-order__cell quick-order__cell--quantity">
      <div class="quick-order__quantity-wrapper">
      <input type="number" class="quick-order__quantity-input" min="0" max="${maxQty}" step="1" value="0"
        data-variant-id="${variant.id}" data-max-qty="${maxQty}" data-quantity-input
          ${!inputEnabled ? 'disabled' : ''}>
        <div class="quick-order__quantity-helper" data-quantity-helper style="display: none;"></div>
        ${allowBackorder && !hasBackorderVariant ? '<div class="quick-order__quantity-error" data-quantity-error>⚠️ Backorder variant missing</div>' : ''}
      </div>
    </td>`;
    
    row.innerHTML = html;
    return row;
  }
  
  /**
   * Format money (simple version - use Shopify's money format if available)
   */
  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
  
  /**
   * Setup Intersection Observer for infinite scroll
   */
  if (scrollSentinel && collections.length > 0) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isLoading && hasMoreProducts) {
          loadMoreProducts();
        }
      });
    }, {
      root: null,
      rootMargin: '200px', // Start loading 200px before sentinel is visible
      threshold: 0
    });
    
    observer.observe(scrollSentinel);
  }
  
  // Initialize
  filterRows();
  calculateLiveSummary();
  
  // Load and display current cart totals on page load
  async function initializeCart() {
    const cart = await getCart();
    if (cart) {
      updateSummaryBar(cart);
      updateCartCount();
    }
  }
  
  initializeCart();
  
  // Listen for global cart:updated events (from cart drawer or other sources)
  document.addEventListener('cart:updated', function() {
    // Re-fetch cart and update summary when cart changes elsewhere
    getCart().then(cart => {
      if (cart) {
        updateSummaryBar(cart);
        updateCartCount();
      }
    });
  });

})();

